import { AfterViewInit, Component, computed, DestroyRef, effect, inject, input, model, output, signal, untracked, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { AvatarComponent } from '../../../../parts/avatar/avatar.component';
import { UserModel } from '../../../../core/models/data/user.model';
import { BootstrapService } from '../../../../core/services/bootstrap.service';
import { ModalActions, ModalTypes } from '../../../../core/enums/base.enums';
import { NavItem } from '../../../../core/models/ui/profile-nav-item.model';
import { UserSessionService } from '../../../../core/services/user-session.service';
import { AuthorizationService } from '../../../../core/services/authorization/authorization.service';
import { AuthorizationStateService } from '../../../../core/services/authorization/authorization-state.service';
import { PermissionUtilities } from '../../../../core/utilities/permission.utilities';
import { UserPermission } from '../../../../core/enums/permissions.enums';
import { AvatarConstants } from '../../../../core/constants/avatar.constants';
import { ConfirmationModalComponent } from "../../../../parts/confirmation-modal/confirmation-modal.component";
import { InfoModalComponent } from "../../../../parts/info-modal/info-modal.component";
import { StatusBadgeComponent } from '../../../../parts/status-badge/status-badge.component';
import { CountryCodePipe, PhoneExtensionPipe, PhonePipe } from '../../../../core/pipes/phone-number/phone-number.pipe';
import { UserService } from '../../../../core/services/user.service';
import { UsersMessagingConstants } from '../../../../core/constants/messaging.constants';
import { TooltipDirective } from '../../../../core/directives/tooltip.directive';
import { IfHasChildPermissionDirective } from '../../../../core/directives/if-has-child-permission/if-has-child-permission.directive';
import { ResponsiveTabsComponent } from '../../../../parts/responsive-tabs/responsive-tabs.component';

@Component({
  selector: 'ms-user-profile-header',
  standalone: true,
  imports: [CommonModule, FontAwesomeModule, AvatarComponent, ConfirmationModalComponent, InfoModalComponent, StatusBadgeComponent, PhonePipe, PhoneExtensionPipe, CountryCodePipe, TooltipDirective, IfHasChildPermissionDirective, ResponsiveTabsComponent],
  templateUrl: './user-profile-header.component.html',
})
export class UserProfileHeaderComponent implements AfterViewInit {
    private phonePipe = new PhonePipe();
    private countryCodePipe = new CountryCodePipe();
    private phoneExtensionPipe = new PhoneExtensionPipe();

    public ModalTypes = ModalTypes;

    public user = model<UserModel | null>();

    // True when the viewed account is the logged-in user; used to hide self deactivate/delete.
    public isOwnAccount = computed<boolean>(() => {
        const user = this.user();
        return !!user && this.userSessionService.isCurrentUser(user.userId);
    });

    // The viewed user's own user-level authority, fetched whenever the viewed user changes —
    // undefined while loading. Populated by the constructor's effect below. Unlike Members,
    // deactivating a user doesn't clear their roles, so Reactivate is protected too, not just
    // Deactivate/Delete/Edit (DEV-2541).
    private targetAuthorityLevel = signal<number | undefined>(undefined);

    // True while the fetch above is in flight (own account excluded — that resolves synchronously
    // with no fetch). Drives a disabled/spinner state on the protected actions so none of them ever
    // look clickable before we actually know the answer.
    private isLoadingTargetAuthorityLevel = signal<boolean>(false);
    public isCheckingProtection = computed<boolean>(() => this.isLoadingTargetAuthorityLevel());

    // The viewed user cannot be reactivated/deactivated/deleted/edited by a viewer whose user-level
    // authority isn't stronger than theirs (e.g. Staff acting on a System Administrator).
    public isProtectedFromModification = computed<boolean>(() => PermissionUtilities.isProtectedFromUserModification(
        this.targetAuthorityLevel(), this.authorizationStateService));

    // General "does the acting user hold this permission at all" checks — a gap here means the
    // option is not applicable to this user and should be hidden entirely, distinct from the
    // target-specific authority-level check above, which disables rather than hides (DEV-2541).
    //
    // Evaluated at the USER (platform) level only. A user is not owned by an organization, so a role
    // held in some organization must not grant platform user management. These previously omitted the
    // organizationId argument, and AuthorizationService.hasPermission answers that by ORing across
    // every organization the viewer belongs to — which surfaced these actions for anyone holding
    // users.* as an org role anywhere (DEV-2643). user-list.component.ts already gates the same
    // three this way.
    private hasUpdatePermission = computed<boolean>(() => PermissionUtilities.hasUserLevelPermission(
        UserPermission.Update, this.authorizationService, this.authorizationStateService));
    private hasStatusPermission = computed<boolean>(() => PermissionUtilities.hasUserLevelPermission(
        UserPermission.Status, this.authorizationService, this.authorizationStateService));
    private hasDeletePermission = computed<boolean>(() => PermissionUtilities.hasUserLevelPermission(
        UserPermission.Delete, this.authorizationService, this.authorizationStateService));

    // Each dropdown item's visibility, named so the template condition and the
    // ifHasChildPermission evaluator below share a single source of truth rather than
    // duplicating these expressions in two places. Visibility reflects only whether the action is
    // applicable to this user at all (general permission, status, self-account rules) — the
    // authority-level protection check does NOT affect visibility, only the disabled state applied
    // in the template (DEV-2541).
    // A user may always edit their own profile, with or without users.update.
    public canShowEditProfile = computed<boolean>(() => this.isOwnAccount() || this.hasUpdatePermission());
    public canShowReactivate = computed<boolean>(() => this.user()?.status === 'Inactive' && this.hasStatusPermission());
    // No separate self-account clause on these two: the permission check is user-level, so anyone who
    // passes it is entitled to act on their own account as well.
    public canShowDeactivate = computed<boolean>(() =>
        this.user()?.status === 'Active' && this.hasStatusPermission());
    // Delete is hidden on the viewer's own account so they can't lock themselves out (DEV-2648).
    // Also gated on a loaded user — otherwise a still-loading header would render an enabled Delete
    // that no-ops when clicked, because confirmUserDeletion() early-returns on null.
    public canShowDelete = computed<boolean>(() => this.hasDeletePermission() && !!this.user() && !this.isOwnAccount());

    public fullName = computed(() => {
        const user = this.user();
        if (!user) return '';
        return `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim();
    });

    public formattedPhone = computed(() => {
        const user = this.user();
        if (!user?.phoneNumber) return '';
        const countryCode = this.countryCodePipe.transform(user.phoneCountryCode);
        const number = this.phonePipe.transform(user.phoneNumber);
        const extension = this.phoneExtensionPipe.transform(user.phoneExtension);
        return [countryCode, number, extension].filter(Boolean).join(' ');
    });

    public isLoading = input(false);
    // The viewed user's platform role (Staff / System Administrator), resolved by the page from the
    // subject's own authorization payload. Undefined when they hold neither, which hides the row
    // entirely rather than leaving an empty label (DEV-2598).
    public platformRole = input<string | undefined>(undefined);
    public navItems = input<NavItem[]>();
    public tabChanged = output<string>();
    public userUpdated = output<void>();
    public editProfileRequested = output<void>();
    
    public avatarSize = AvatarConstants.SIZE().PROFILE_PAGE;
    public avatarShape = AvatarConstants.SHAPE().PROFILE_PAGE;
    public avatarAdditionalAttributes = AvatarConstants.ADDITIONAL_ATTRIBUTES().PROFILE_PAGE;

    @ViewChild('userProfileHeaderConfirmationModalRef') userProfileHeaderConfirmationModalRef!: ConfirmationModalComponent;
    @ViewChild('userProfileHeaderInfoModalRef') userProfileHeaderInfoModalRef!: InfoModalComponent;
    @ViewChild(IfHasChildPermissionDirective) private actionsDropdownVisibility!: IfHasChildPermissionDirective;

    private destroyRef = inject(DestroyRef);

    public confirmationCancelFunction: Function = () => {};
    public confirmationConfirmFunction: Function = () => {};
    
    public isSubmitting = false;

    constructor(
        private bootstrapService: BootstrapService, 
        private userSessionService: UserSessionService,
        private userService: UserService,
        private authorizationService: AuthorizationService,
        private authorizationStateService: AuthorizationStateService
    ) {
        // Refetch the viewed user's authority level whenever the viewed user changes. Guarded by
        // userId so a slow, stale fetch from a previously-viewed user can't overwrite a newer one.
        // Self-actions bypass the authority check entirely (matching the backend's own
        // `actingUserId != targetUserId` guard) — a user's authority level compared against itself
        // is always a tie, which would otherwise be misread as unauthorized.
        effect(() => {
            const user = this.user();
            const userId = user?.userId;

            untracked(() => {
                this.targetAuthorityLevel.set(undefined);

                if (userId === undefined || this.isOwnAccount()) {
                    this.isLoadingTargetAuthorityLevel.set(false);
                    return;
                }

                this.isLoadingTargetAuthorityLevel.set(true);
                PermissionUtilities.fetchTargetUserAuthorityLevel(userId, this.authorizationService)
                    .then(level => {
                        if (this.user()?.userId === userId) {
                            this.targetAuthorityLevel.set(level);
                            this.isLoadingTargetAuthorityLevel.set(false);
                        }
                    });
            });
        });
    }

    // Registers with ifHasChildPermission (on the dropdown wrapper in the template) so the whole
    // Actions button hides itself once every item is hidden — e.g. a fully protected user —
    // instead of opening onto an empty menu (DEV-2541).
    public ngAfterViewInit(): void {
        const evaluator = () => this.canShowEditProfile() || this.canShowReactivate() || this.canShowDeactivate() || this.canShowDelete();

        this.actionsDropdownVisibility.register(evaluator);
        this.destroyRef.onDestroy(() => this.actionsDropdownVisibility.unregister(evaluator));
    }

    // #region Avatar
    public onAvatarChanged(updatedUser: any): void {
        if (this.user()) {
            const updatedUserModel: UserModel = {
                ...this.user()!,
                avatarFileId: updatedUser.avatarFileId
            };
            this.user.set(updatedUserModel);
            
            const activeUser = this.userSessionService.activeUser();
            if (activeUser && activeUser.userId === updatedUserModel.userId) {
                this.userSessionService.updateActiveUser(updatedUserModel);
            }
        }
    }

    public onAvatarRemoved(): void {
        this.userProfileHeaderConfirmationModalRef.confirmButtonText.set("Delete");
        this.userProfileHeaderConfirmationModalRef.modalType.set(ModalTypes.Question);
        this.userProfileHeaderConfirmationModalRef.message.set(UsersMessagingConstants.USERS_DELETE_AVATAR());
        
        this.confirmationConfirmFunction = this.removeAvatarConfirmed;
        this.confirmationCancelFunction = this.removeAvatarCancelled;
        
        this.bootstrapService.manageModal("#userProfileHeaderConfirmationModal", ModalActions.Show);
    }

    public async removeAvatarConfirmed(): Promise<void> {
        this.isSubmitting = true;
        try {
            const response = await this.userService.updateUser(
                {
                    firstName: this.user()!.firstName, 
                    lastName: this.user()!.lastName,
                    phoneCountryCode: this.user()!.phoneCountryCode || null,
                    phoneNumber: this.user()!.phoneNumber || null,
                    phoneExtension: this.user()!.phoneExtension || null,
                    avatarHexColor: this.user()!.avatarHexColor!,
                    clearAvatarFileId: true
                }, this.user()!.userId);

            this.bootstrapService.manageModal("#userProfileHeaderConfirmationModal", ModalActions.Hide);

            if (this.user() && response.success && response.data) {
                const updatedUserModel: UserModel = {
                    ...this.user()!,
                    avatarFileId: undefined
                };
                this.user.set(updatedUserModel);
                
                const activeUser = this.userSessionService.activeUser();
                if (activeUser && activeUser.userId === updatedUserModel.userId) {
                    this.userSessionService.updateActiveUser(updatedUserModel);
                }

                this.userProfileHeaderInfoModalRef.modalType.set(ModalTypes.Success);
                this.userProfileHeaderInfoModalRef.message.set(UsersMessagingConstants.USERS_DELETE_AVATAR_SUCCESS());
                this.bootstrapService.manageModal("#userProfileHeaderInfoModal", ModalActions.Show);
            } else {
                this.userProfileHeaderInfoModalRef.modalType.set(ModalTypes.Failure);
                this.userProfileHeaderInfoModalRef.message.set(response.message || UsersMessagingConstants.USERS_ERROR_DELETE_AVATAR());
                this.bootstrapService.manageModal("#userProfileHeaderInfoModal", ModalActions.Show);
            }
        } finally {
            this.isSubmitting = false;
        }
    }

    public removeAvatarCancelled(): void {
        this.bootstrapService.manageModal("#userProfileHeaderConfirmationModal", ModalActions.Hide);
    }
    // #endregion Avatar
        
    // #region Toggle Activation
    public openReactivateModal(user: UserModel): void {
        this.userProfileHeaderConfirmationModalRef.confirmButtonText.set("Reactivate");
        this.userProfileHeaderConfirmationModalRef.modalType.set(ModalTypes.Question);
        this.userProfileHeaderConfirmationModalRef.enableNotes.set(true);
        this.userProfileHeaderConfirmationModalRef.isNoteRequired.set(false);

        const confirmationMessageData = {actionText: "activate", firstName: user.firstName, lastName: user.lastName};
        const confirmationMessage = 
            UsersMessagingConstants.USERS_UPDATE_ACTIVATION_STATUS_CONFIRMATION().replaceValuesWithObject(confirmationMessageData);

        this.userProfileHeaderConfirmationModalRef.message.set(confirmationMessage);
        this.confirmationCancelFunction = this.onToggleActivationCanceled;
        this.confirmationConfirmFunction = this.onReactivateConfirmed;
        this.bootstrapService.manageModal("#userProfileHeaderConfirmationModal", ModalActions.Show);
    }

    public openDeactivateModal(user: UserModel): void {
        this.userProfileHeaderConfirmationModalRef.confirmButtonText.set("Deactivate");
        this.userProfileHeaderConfirmationModalRef.modalType.set(ModalTypes.Question);
        this.userProfileHeaderConfirmationModalRef.enableNotes.set(true);
        this.userProfileHeaderConfirmationModalRef.isNoteRequired.set(false);

        const confirmationMessageData = {actionText: "deactivate", firstName: user.firstName, lastName: user.lastName};
        const confirmationMessage = 
            UsersMessagingConstants.USERS_UPDATE_ACTIVATION_STATUS_CONFIRMATION().replaceValuesWithObject(confirmationMessageData);

        this.userProfileHeaderConfirmationModalRef.message.set(confirmationMessage);
        this.confirmationCancelFunction = this.onToggleActivationCanceled;
        this.confirmationConfirmFunction = this.onDeactivateConfirmed;
        this.bootstrapService.manageModal("#userProfileHeaderConfirmationModal", ModalActions.Show);
    }

    public onToggleActivationCanceled = () => {
        this.bootstrapService.manageModal("#userProfileHeaderConfirmationModal", ModalActions.Hide);
    }

    public onReactivateConfirmed = async (note: string | undefined) => {
        const user = this.user();
        if (!user) return;

        this.isSubmitting = true;
        try {
            const response = await this.userService.updateUserStatus(user.userId, true, note);

            if (response.success === false) {
                this.userProfileHeaderInfoModalRef.modalType.set(ModalTypes.Failure);
            } else {
                this.userProfileHeaderInfoModalRef.modalType.set(ModalTypes.Success);
                this.userUpdated.emit();
            }

            this.userProfileHeaderInfoModalRef.message.set(response.message);
            this.bootstrapService.manageModal("#userProfileHeaderInfoModal", ModalActions.Show);
            this.bootstrapService.manageModal("#userProfileHeaderConfirmationModal", ModalActions.Hide);
        } finally {
            this.isSubmitting = false;
        }
    }

    public onDeactivateConfirmed = async (note: string | undefined) => {
        const user = this.user();
        if (!user) return;

        this.isSubmitting = true;
        try {
            const response = await this.userService.updateUserStatus(user.userId, false, note);

            if (response.success === false) {
                this.userProfileHeaderInfoModalRef.modalType.set(ModalTypes.Failure);
            } else {
                this.userProfileHeaderInfoModalRef.modalType.set(ModalTypes.Success);
                this.userUpdated.emit();
            }

            this.userProfileHeaderInfoModalRef.message.set(response.message);
            this.bootstrapService.manageModal("#userProfileHeaderInfoModal", ModalActions.Show);
            this.bootstrapService.manageModal("#userProfileHeaderConfirmationModal", ModalActions.Hide);
        } finally {
            this.isSubmitting = false;
        }
    }
    // #endregion Toggle Activation

    // #region Delete User
    public confirmUserDeletion(): void {
        const user = this.user();
        if (!user) return;

        const confirmationMessageData = {firstName: user.firstName, lastName: user.lastName};
        const confirmationMessage = 
            UsersMessagingConstants.USERS_DELETE_USER_CONFIRMATION().replaceValuesWithObject(confirmationMessageData);
        
        this.userProfileHeaderConfirmationModalRef.confirmButtonText.set("Delete");
        this.userProfileHeaderConfirmationModalRef.modalType.set(ModalTypes.Question);
        this.userProfileHeaderConfirmationModalRef.message.set(confirmationMessage);
        this.userProfileHeaderConfirmationModalRef.enableNotes.set(true);    
        this.userProfileHeaderConfirmationModalRef.isNoteRequired.set(false);

        this.confirmationConfirmFunction = this.confirmedUserDeletionConfirmation;
        this.confirmationCancelFunction = this.cancelUserDeletionConfirmation;

        this.bootstrapService.manageModal("#userProfileHeaderConfirmationModal", ModalActions.Show);
    }

    public confirmedUserDeletionConfirmation = async (note: string | undefined) => {
        const user = this.user();
        if (!user) return;

        this.isSubmitting = true;
        try {
            const response = await this.userService.deleteUser(user.userId, note);

            if (response.success === false) {
                this.userProfileHeaderInfoModalRef.modalType.set(ModalTypes.Failure);
            } else {
                this.userProfileHeaderInfoModalRef.modalType.set(ModalTypes.Success);
                this.userUpdated.emit();
            }
            
            this.userProfileHeaderInfoModalRef.message.set(response.message);
            this.bootstrapService.manageModal("#userProfileHeaderInfoModal", ModalActions.Show);
            this.bootstrapService.manageModal("#userProfileHeaderConfirmationModal", ModalActions.Hide);
        } finally {
            this.isSubmitting = false;
        }
    }

    public cancelUserDeletionConfirmation = () => {
        this.bootstrapService.manageModal("#userProfileHeaderConfirmationModal", ModalActions.Hide);
    }
    // #endregion Delete User

    public onTabClick(tabId: string): void {
        this.tabChanged.emit(tabId);
    }
}
